// ---------------------------------------------------------------------------
// Select — presentational dropdown field (props in → UI out).
//
// Designsystemet owns the native select and its accessible field wiring.
// Existing classes remain layout hooks. No data fetching or auth logic.
//
// All user-facing label/hint/error text is supplied by the caller (already
// i18n-translated by the consuming page); this component holds no Norwegian
// strings.
// ---------------------------------------------------------------------------

import { useId } from 'react'
import { Field, Label, Select as DsSelect, ValidationMessage } from '@digdir/designsystemet-react'

export type SelectOption = {
  value: string
  label: string
}

/**
 * Optional content-width cap (desktop only; fields go full-width and stack at
 * ≤640px). Maps to the `.field--w-*` token classes in components.css.
 *   xs ~14ch · s ~22ch · m ~34ch · l ~46ch
 */
export type FieldSize = 'xs' | 's' | 'm' | 'l'

export type SelectProps = {
  /** Stable control ID for links from an error summary; generated when omitted. */
  id?: string
  /** Visible label text (pre-translated by caller). */
  label: string
  /** Keep the accessible name while hiding the visible label. */
  hideLabel?: boolean
  /** Controlled value — the currently selected option's value. */
  value: string
  /** Called with the new value string when the user picks a different option. */
  onChange: (value: string) => void
  /** Options rendered as <option> elements. */
  options: SelectOption[]
  /** Optional helper text between the label and select. */
  hint?: string
  /** Validation error message. Triggers aria-invalid. */
  error?: string
  /** Marks the select as required (browser-native + aria). */
  required?: boolean
  /** Disables the select control. */
  disabled?: boolean
  /** Optional content-width cap (desktop only). Default: full width. */
  size?: FieldSize
}

/**
 * Presentational select field with label, optional hint and error message.
 *
 * Designsystemet owns control styling; `.field` and `.select` retain layout
 * hooks. Consumers import the wrapper from `shared/components`.
 *
 * @example
 * <Select
 *   label={t('case.status')}
 *   value={status}
 *   onChange={setStatus}
 *   options={statusOptions}
 *   error={errors.status}
 * />
 */
export function Select({
  id,
  label,
  hideLabel = false,
  value,
  onChange,
  options,
  hint,
  error,
  required = false,
  disabled = false,
  size,
}: SelectProps) {
  const baseId = useId()
  const selectId = id ?? `${baseId}select`
  const errorId = `${selectId}-err`
  const hintId = `${selectId}-hint`

  return (
    <Field className={size ? `field field--w-${size}` : 'field'}>
      <Label htmlFor={selectId} className={hideLabel ? 'sr-only' : 'field__lbl'}>
        {label}
      </Label>

      {hint && <Field.Description id={hintId} className="field__hint">{hint}</Field.Description>}

      <DsSelect
        id={selectId}
        className="select"
        value={value}
        onChange={disabled ? undefined : (e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={error ? 'true' : undefined}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </DsSelect>

      {error && (
        <ValidationMessage id={errorId} className="field__err" role="alert">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}
